/**
 * VisitRequestManager
 * Toont alle bezoekverzoeken (pending + geaccepteerd) voor clubbeheerders.
 * Elke bezoeker heeft een profielknop die via het globale openUserProfile event
 * de profilepagina opent in de Mijn Verhaal weergave.
 */

import React, { useState, useEffect } from 'react';
import { auth, db } from '../../../firebase';
import { collection, query, where, getDocs, updateDoc, doc, serverTimestamp, addDoc } from 'firebase/firestore';
import { ClubVisitRequest } from '../../../types/networkclub';
import { Icon } from '../../icons';
import { CheckCircle, XCircle, Calendar, User, Building2, Loader, Phone } from '../../icons';

interface VisitRequestManagerProps {
  clubId: string;
  entityId: string;
  accentColor?: string;
}

const openUserProfile = (userId: string) => {
  if (!userId) return;
  window.dispatchEvent(new CustomEvent('openUserProfile', { detail: { userId } }));
};

const VisitRequestManager: React.FC<VisitRequestManagerProps> = ({ clubId, entityId, accentColor = '#7A9E8E' }) => {
  const [requests, setRequests] = useState<ClubVisitRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'pending' | 'accepted' | 'all'>('all');

  useEffect(() => {
    loadRequests();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const requestsQuery = query(
        collection(db, 'clubVisitRequests'),
        where('clubId', '==', clubId),
      );
      const snapshot = await getDocs(requestsQuery);
      const data: ClubVisitRequest[] = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        visitDate: d.data().visitDate?.toDate() || new Date(),
        createdAt: d.data().createdAt?.toDate() || new Date(),
        respondedAt: d.data().respondedAt?.toDate() || undefined,
      } as ClubVisitRequest));
      data.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      setRequests(data);
    } catch (error) {
      console.error('Error loading visit requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (requestId: string, accept: boolean) => {
    setProcessingId(requestId);
    try {
      const user = auth.currentUser;
      if (!user) return;
      await updateDoc(doc(db, 'clubVisitRequests', requestId), {
        status: accept ? 'accepted' : 'declined',
        respondedAt: serverTimestamp(),
        respondedBy: user.uid,
      });
      // Notify the visitor via inbox
      const request = requests.find(r => r.id === requestId);
      if (request?.visitorId) {
        await addDoc(collection(db, 'inboxItems'), {
          userId: request.visitorId,
          type: 'notification',
          category: 'planning',
          title: accept ? '✅ Bezoekverzoek geaccepteerd' : '❌ Bezoekverzoek afgewezen',
          message: accept
            ? `Je bezoekverzoek voor ${request.visitDate.toLocaleDateString('nl-NL')} is geaccepteerd!`
            : `Je bezoekverzoek voor ${request.visitDate.toLocaleDateString('nl-NL')} is helaas niet gehonoreerd.`,
          isRead: false,
          priority: accept ? 'high' : 'medium',
          createdAt: serverTimestamp(),
          actionData: { actionType: 'accept_invitation', visitRequestId: requestId, clubId },
        });
      }
      await loadRequests();
    } catch (error) {
      console.error('Error responding to visit request:', error);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Icon icon={Loader} size={22} strokeWidth={1.5} className="animate-spin text-orange-400" />
      </div>
    );
  }

  const filtered = filter === 'all'
    ? requests
    : requests.filter(r => r.status === filter);

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const acceptedCount = requests.filter(r => r.status === 'accepted').length;

  return (
    <div className="space-y-4">
      {/* Filter tabs */}
      <div className="flex items-center gap-1">
        {([
          { key: 'all',      label: `Alle (${requests.length})` },
          { key: 'pending',  label: `Openstaand (${pendingCount})` },
          { key: 'accepted', label: `Geaccepteerd (${acceptedCount})` },
        ] as const).map(t => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            style={filter === t.key ? { background: accentColor, borderColor: accentColor, color: '#fff' } : undefined}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${filter !== t.key ? 'bg-white border-gray-200 text-gray-600 hover:opacity-70' : ''}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-400">
          Geen bezoekverzoeken gevonden
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(request => (
            <div
              key={request.id}
              className={`rounded-xl border p-4 transition-all ${
                request.status === 'accepted'
                  ? 'border-green-200 bg-green-50'
                  : request.status === 'declined'
                  ? 'border-gray-200 bg-gray-50 opacity-60'
                  : 'border-orange-100 bg-white hover:border-orange-200'
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Avatar */}
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                  style={{ backgroundColor: '#f97316' }}
                  onClick={() => openUserProfile(request.visitorId)}
                  title="Bekijk profiel"
                >
                  {(request.visitorName || 'B')[0].toUpperCase()}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => openUserProfile(request.visitorId)}
                      className="text-sm font-semibold text-gray-900 hover:text-orange-600 transition-colors text-left"
                    >
                      {request.visitorName || 'Onbekend'}
                    </button>
                    {/* Status badge */}
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      request.status === 'accepted'
                        ? 'bg-green-100 text-green-700'
                        : request.status === 'declined'
                        ? 'bg-gray-100 text-gray-500'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {request.status === 'accepted' ? '✓ Geaccepteerd' : request.status === 'declined' ? 'Afgewezen' : '⏳ Openstaand'}
                    </span>
                  </div>

                  <div className="mt-1 space-y-0.5 text-xs text-gray-500">
                    {request.visitorEmail && (
                      <p>{request.visitorEmail}</p>
                    )}
                    {request.company && (
                      <p className="flex items-center gap-1">
                        <Building2 size={11} /> {request.company}
                      </p>
                    )}
                    {request.phone && (
                      <p className="flex items-center gap-1">
                        <Phone size={11} /> {request.phone}
                      </p>
                    )}
                    <p className="flex items-center gap-1">
                      <Calendar size={11} />
                      {request.visitDate.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })}
                      {' '}om {request.visitDate.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    {request.purpose && (
                      <p className="mt-1 text-gray-600 italic">"{request.purpose}"</p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2 flex-shrink-0">
                  {/* Profile button — always visible */}
                  <button
                    onClick={() => openUserProfile(request.visitorId)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-colors"
                    style={{ backgroundColor: '#7A9488' }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#4F6660')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#7A9488')}
                    title="Profiel bekijken"
                  >
                    <User size={12} />
                    Profiel
                  </button>

                  {/* Accept / Decline — only for pending */}
                  {request.status === 'pending' && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleResponse(request.id, true)}
                        disabled={processingId === request.id}
                        className="flex-1 p-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50"
                        title="Accepteren"
                      >
                        {processingId === request.id ? (
                          <Icon icon={Loader} size={14} strokeWidth={1.5} className="animate-spin mx-auto" />
                        ) : (
                          <Icon icon={CheckCircle} size={14} strokeWidth={1.5} className="mx-auto" />
                        )}
                      </button>
                      <button
                        onClick={() => handleResponse(request.id, false)}
                        disabled={processingId === request.id}
                        className="flex-1 p-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50"
                        title="Afwijzen"
                      >
                        <Icon icon={XCircle} size={14} strokeWidth={1.5} className="mx-auto" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VisitRequestManager;
